import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import type { MarketRow } from '../data/marketPerformanceData';
import {
  formatMarketMillions,
  formatMarketRate,
  formatMarketThousands,
  formatMarketUplift,
  formatMarketWow,
} from '../data/utils';
import { InfoTip } from '../shared/InfoTip';

interface Tile {
  label: string;
  value: string;
  tip: string;
  strong?: boolean;
  sub?: string;
}

function buildTiles(market: MarketRow, currencySymbol: string): Tile[] {
  return [
    {
      label: 'Potential savings',
      value: formatMarketThousands(market.potentialSavingsK, currencySymbol),
      sub: `${formatMarketRate(market.savingsRate)} of spend`,
      strong: true,
      tip: 'The total budget that could be saved, currently spent on creatives performing below their benchmark. A lower number indicates less wasted budget.',
    },
    {
      label: 'Performance index',
      value: String(market.performanceIndex),
      tip: 'An efficiency score for this market, where higher scores represent less waste and a score of 100 serves as the cross-market baseline.',
    },
    {
      label: 'Potential Uplift',
      value: formatMarketUplift(market.potentialUpliftK, currencySymbol),
      tip: 'The estimated performance gain you can unlock by optimizing your ad creatives based on these recommendations.',
    },
    {
      label: 'Total spend',
      value: formatMarketMillions(market.spendMillions, currencySymbol),
      tip: 'Your total advertising spend in this market during the selected time period.',
    },
    {
      label: 'WoW efficiency',
      value: market.weekOverWeek === null ? '-' : formatMarketWow(market.weekOverWeek),
      tip: 'The change in efficiency compared to the previous week, where a positive percentage indicates a reduction in wasted spend. A dash appears if there is no prior data to compare.',
    },
    {
      label: 'Leaderboard rank',
      value: `#${market.rank}`,
      tip: 'How this market ranks against all others based on potential savings, where markets with lower wasted spend are ranked higher.',
    },
  ];
}

function MetricTile({ tile, index }: { tile: Tile; index: number }): ReactNode {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: 11,
          color: 'text.secondary',
        }}
      >
        {tile.label}
        <InfoTip text={tile.tip} placement={index % 3 === 0 ? 'right' : 'left'} />
      </Box>
      <Typography
        sx={
          tile.strong
            ? (theme) => ({
                ...theme.mixins.gradientText,
                fontSize: 22,
                fontWeight: 800,
                mt: 1,
              })
            : { fontSize: 18, fontWeight: 800, color: 'text.primary', mt: 1 }
        }
      >
        {tile.value}
      </Typography>
      {tile.sub && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>{tile.sub}</Typography>
      )}
    </Box>
  );
}

export function MarketTiles({
  market,
  currencySymbol,
}: {
  market: MarketRow;
  currencySymbol: string;
}): ReactNode {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 2,
        mt: 1,
      }}
    >
      {buildTiles(market, currencySymbol).map((tile, index) => (
        <MetricTile key={tile.label} tile={tile} index={index} />
      ))}
    </Box>
  );
}
